/**
 * Eddie Borgo — Infinite Scroll v2
 * High-performance infinite scroll for collection and blog pages.
 *
 * Key improvements over v1:
 * - Section Rendering API (~85% smaller payloads)
 * - Prefetch queue (next page cached during idle)
 * - Skeleton placeholder UI (instant visual feedback)
 * - Momentum scroll protection (catches fast iOS flicks)
 * - Progressive image loading (viewport-aware, no burst)
 */
(function () {
  'use strict';

  var SENTINEL_CLASS = 'eb-infinite-scroll__sentinel';
  var SPINNER_CLASS = 'eb-infinite-scroll__spinner';
  var SPINNER_VISIBLE = 'eb-infinite-scroll__spinner--visible';
  var END_CLASS = 'eb-infinite-scroll__end';
  var RETRY_CLASS = 'eb-infinite-scroll__retry';
  var STORAGE_KEY = 'eb-scroll-state';
  var PRELOAD_BUFFER = 4000;       // px before bottom to trigger load
  var FETCH_TIMEOUT = 10000;       // ms before aborting a page fetch
  var VELOCITY_THRESHOLD = 3000;   // px/sec to trigger momentum load
  var RESTORE_MAX_PAGES = 10;      // deepest restore before we stop refetching
  var RESTORE_CONCURRENCY = 3;     // page fetches in flight during a restore
  var IMAGE_ROOT_MARGIN = '800px'; // load an image this far before it scrolls in
  var ANCHOR_GAP = 12;             // breathing room under the sticky header

  var state = {
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    observer: null,
    sentinel: null,
    spinner: null,
    grid: null,
    container: null,
    abortController: null,
    scrollHandler: null,
    velocityHandler: null,
    sectionId: null,
    prefetchedPages: {},
    prefetchController: null,
    prefetchInFlight: null,
    skeletons: [],
    skeletonTemplate: null,
    imageObserver: null,
    isBlog: false,
    isCollectionList: false
  };

  /* ─── Init ─── */

  function init() {
    state.container = document.querySelector('[data-eb-infinite-scroll="true"]');
    if (!state.container) return;

    state.grid = state.container.querySelector('.collection-content__wrapper .product-list') ||
                 state.container.querySelector('.product-list') ||
                 state.container.querySelector('.eb-blog-grid') ||
                 state.container.querySelector('section.collections');
    if (!state.grid) return;

    state.isBlog = state.grid.classList.contains('eb-blog-grid');
    state.isCollectionList = state.grid.classList.contains('collections');

    var pagination = state.container.querySelector('.pagination[data-eb-total-pages]');
    if (!pagination) return;

    state.totalPages = parseInt(pagination.getAttribute('data-eb-total-pages'), 10) || 1;
    state.currentPage = parseInt(pagination.getAttribute('data-eb-current-page'), 10) || 1;

    // Read section ID for Section Rendering API
    state.sectionId = state.container.getAttribute('data-eb-section-id') || null;

    // Cache the skeleton template (none for collection-list — no skeleton needed)
    if (state.isCollectionList) {
      state.skeletonTemplate = null;
    } else if (state.isBlog) {
      state.skeletonTemplate = document.getElementById('eb-skeleton-blog-card');
    } else {
      state.skeletonTemplate = document.getElementById('eb-skeleton-card');
    }

    if (state.totalPages <= 1 || state.currentPage >= state.totalPages) return;

    // Mark first-page items so reinit skips them (products only)
    if (!state.isBlog && !state.isCollectionList) {
      state.grid.querySelectorAll('[data-product-item]').forEach(function (el) {
        el.setAttribute('data-eb-initialized', 'true');
      });
    }

    pagination.style.display = 'none';

    /* Put the customer back where they were — but only if they actually came
       back. Restoring on a fresh click into the collection drops them ten
       screens down a grid they never asked to see, and because the saved key
       carries the query string a filter or sort change now counts as a
       different view rather than a return to the old one. */
    var saved = getSavedState();
    if (saved && saved.path === locationKey() && saved.page > 1) {
      if (isReturnNavigation()) {
        /* Take scroll restoration off the browser for this one case so it
           stops fighting the anchor we are about to set. Everywhere else
           'auto' stays: the browser handles shallow pages and bfcache
           returns better than this script can. */
        try { history.scrollRestoration = 'manual'; } catch (e) { /* unsupported */ }
        restorePages(saved);
        return;
      }
      clearState();
    }

    createSpinner();
    createSentinel();
    connectObserver();
    connectVelocityWatch();
    saveScrollPosition();
    schedulePrefetch();
  }

  /* ─── Session State ─── */

  /* Filters and sort navigate to the same path with a different query string,
     so the path alone cannot tell a return visit from a new set of results. */
  function locationKey() {
    return window.location.pathname + window.location.search;
  }

  /* Back, forward or a refresh means "I was already here". A plain click into
     the collection does not, however deep the last visit went. */
  function isReturnNavigation() {
    try {
      var entries = performance.getEntriesByType('navigation');
      if (entries && entries.length && entries[0].type) {
        return entries[0].type === 'back_forward' || entries[0].type === 'reload';
      }
      // Safari < 15 and other stragglers.
      if (performance.navigation) {
        return performance.navigation.type === 1 || performance.navigation.type === 2;
      }
    } catch (e) { /* Timing API unavailable */ }
    // Unknown navigation type — start at the top rather than guess wrong.
    return false;
  }

  function gridItemSelector() {
    if (state.isCollectionList) return '.collection-wrap';
    if (state.isBlog) return 'article.eb-blog-card';
    return 'li[data-product-item]';
  }

  /* A stable identity for one tile. Position in the grid is not one: a filter,
     a sort or a sold-out product changes what sits at index N, and the
     customer asked to come back to a thing rather than to a slot. Products
     carry their id in a class; blog and collection cards are identified by
     what they link to. */
  function itemKey(el) {
    if (!el) return null;

    var idMatch = String(el.className).match(/product-(\d+)/);
    if (idMatch) return 'id:' + idMatch[1];

    var link = el.querySelector('a[href]');
    if (!link) return null;

    var path = (link.getAttribute('href') || '').split('#')[0].split('?')[0].replace(/\/+$/, '');
    var slug = path.substring(path.lastIndexOf('/') + 1);
    return slug ? 'slug:' + slug : null;
  }

  function findByKey(items, key) {
    if (!key) return null;
    for (var i = 0; i < items.length; i++) {
      if (itemKey(items[i]) === key) return items[i];
    }
    return null;
  }

  function getSavedState() {
    try {
      var data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  function saveState() {
    try {
      var productIndex = 0;
      var items = state.grid ? state.grid.querySelectorAll(gridItemSelector()) : [];
      for (var i = 0; i < items.length; i++) {
        var rect = items[i].getBoundingClientRect();
        if (rect.top >= 0) {
          productIndex = i;
          break;
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        path: locationKey(),
        page: state.currentPage,
        productIndex: productIndex,
        // The index is kept only as a fallback for when the tile itself is gone.
        itemKey: itemKey(items[productIndex])
      }));
    } catch (e) { /* Storage unavailable */ }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* Storage unavailable */ }
  }

  function saveScrollPosition() {
    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
    }
    var scrollTimer = null;
    state.scrollHandler = function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        if (state.currentPage > 1) {
          saveState();
        }
      }, 200);
    };
    window.addEventListener('scroll', state.scrollHandler);
  }

  /* ─── Scroll Anchoring ─── */

  /* How much of the viewport the header actually covers once it is pinned.
     Atlantic parks it at a negative offset, so only the part below that line
     obscures the grid — measure it rather than assuming the full height. */
  function stickyHeaderOffset() {
    var header = document.querySelector('.main-header-wrap');
    if (!header) return 0;

    var styles = window.getComputedStyle(header);
    if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0;

    var covered = header.getBoundingClientRect().height + Math.min(0, parseFloat(styles.top) || 0);
    return covered > 0 ? covered : 0;
  }

  function scrollItemIntoPlace(el) {
    var top = window.scrollY + el.getBoundingClientRect().top - stickyHeaderOffset() - ANCHOR_GAP;
    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  }

  /* Images land after the markup, so the row the customer wants keeps moving
     for a moment after the first attempt. Re-seat it a few times — and stop
     the instant they take over by scrolling themselves, because fighting a
     customer for control of the scrollbar is worse than landing slightly off. */
  function anchorToItem(el) {
    scrollItemIntoPlace(el);

    var checks = [50, 150, 350, 700, 1200];
    var timers = [];
    var released = false;

    function release() {
      if (released) return;
      released = true;
      timers.forEach(clearTimeout);
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchstart', release);
      window.removeEventListener('keydown', release);
    }

    window.addEventListener('wheel', release, { passive: true });
    window.addEventListener('touchstart', release, { passive: true });
    window.addEventListener('keydown', release);

    checks.forEach(function (delay, i) {
      timers.push(setTimeout(function () {
        if (released) return;
        if (el.isConnected) scrollItemIntoPlace(el);
        if (i === checks.length - 1) release();
      }, delay));
    });
  }

  /* ─── Restore Pages (return to a collection) ─── */

  /* Walk the intermediate pages a few at a time. Firing all of them at once
     buried the browser's connection pool on a long collection, and everything
     behind them — including the images the customer is about to look at —
     queued behind that burst. */
  function fetchPagesInOrder(pages) {
    var results = new Array(pages.length);
    var next = 0;

    function worker() {
      if (next >= pages.length) return Promise.resolve();
      var slot = next++;
      return fetch(buildUrl(pages[slot]))
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; })
        .then(function (html) {
          results[slot] = html;
          return worker();
        });
    }

    var lanes = Math.min(RESTORE_CONCURRENCY, pages.length);
    var workers = [];
    for (var i = 0; i < lanes; i++) workers.push(worker());

    return Promise.all(workers).then(function () { return results; });
  }

  function restorePages(saved) {
    /* Somebody who got twenty pages deep does not want to sit through twenty
       refetches to come back. Rebuild what is reasonable and let them carry
       on scrolling from there. */
    var targetPage = Math.min(saved.page, RESTORE_MAX_PAGES);
    var wasCapped = saved.page > targetPage;

    clearState();

    var pagination = state.container.querySelector('.pagination[data-eb-total-pages]');
    if (pagination) pagination.style.display = 'none';

    var pages = [];
    for (var p = 2; p <= targetPage; p++) pages.push(p);

    fetchPagesInOrder(pages)
      .then(function (results) {
        results.forEach(function (html) {
          if (html) appendItemsFromHtml(html);
        });

        state.currentPage = targetPage;
        reinitComponents();
        updateRelNext();

        var items = state.grid.querySelectorAll(gridItemSelector());
        var target = findByKey(items, saved.itemKey);
        if (!target) {
          // Their tile is gone, or sits beyond the depth we rebuilt.
          target = wasCapped ? items[items.length - 1] : items[saved.productIndex || 0];
        }
        if (target) anchorToItem(target);

        resumeInfiniteScroll();
      })
      .catch(resumeInfiniteScroll);
  }

  function resumeInfiniteScroll() {
    if (state.currentPage >= state.totalPages) return;
    createSpinner();
    createSentinel();
    connectObserver();
    connectVelocityWatch();
    saveScrollPosition();
    schedulePrefetch();
  }

  /* ─── Parse & Append ─── */

  function appendItemsFromHtml(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    var fetchedGrid;
    var items;

    if (state.isCollectionList) {
      fetchedGrid = doc.querySelector('section.collections');
      items = fetchedGrid ? fetchedGrid.querySelectorAll('.collection-wrap') : [];
    } else if (state.isBlog) {
      fetchedGrid = doc.querySelector('.eb-blog-grid');
      items = fetchedGrid ? fetchedGrid.querySelectorAll('article.eb-blog-card') : [];
    } else {
      fetchedGrid = doc.querySelector('.collection-content__wrapper .product-list') ||
                    doc.querySelector('.product-list');
      items = fetchedGrid ? fetchedGrid.querySelectorAll('li[data-product-item]') : [];
    }

    if (!fetchedGrid || items.length === 0) return;

    var fragment = document.createDocumentFragment();
    var appended = [];
    items.forEach(function (item) {
      var clone = item.cloneNode(true);
      appended.push(clone);
      fragment.appendChild(clone);
    });
    state.grid.appendChild(fragment);

    observeLazyImages(appended);

    return appended;
  }

  /* ─── Image Loading ─── */

  /**
   * Load appended images as they approach the viewport.
   *
   * The theme's own rimg loader never sees nodes we append, so these images
   * would sit on their placeholder forever if we left them alone — which is
   * why this used to force-load the entire grid on every append. That cost a
   * request per image the moment it existed; restoring ten pages meant a few
   * hundred at once. The rimg placeholder already carries the real image's
   * aspect ratio, so a row holds its height until the picture arrives and
   * nothing shifts under the reader while we wait.
   */
  function observeLazyImages(roots) {
    var images = collectLazyImages(roots);
    if (!images.length) return;

    if (!('IntersectionObserver' in window)) {
      images.forEach(forceLoadImage);
      return;
    }

    if (!state.imageObserver) {
      state.imageObserver = new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          forceLoadImage(entry.target);
        });
      }, { rootMargin: IMAGE_ROOT_MARGIN });
    }

    images.forEach(function (img) {
      state.imageObserver.observe(img);
    });
  }

  function collectLazyImages(roots) {
    var images = [];
    (roots || []).forEach(function (root) {
      if (root.matches && root.matches('img[data-rimg="lazy"]')) {
        images.push(root);
      }
      if (root.querySelectorAll) {
        root.querySelectorAll('img[data-rimg="lazy"]').forEach(function (img) {
          images.push(img);
        });
      }
    });
    return images;
  }

  function forceLoadImage(img) {
    var src = img.getAttribute('src');
    if (src && src.indexOf('data:image') === -1) {
      img.removeAttribute('srcset');
      img.setAttribute('data-rimg', 'loaded');
    } else {
      var template = img.getAttribute('data-rimg-template');
      var max = img.getAttribute('data-rimg-max');
      if (template && max) {
        var parts = max.split('x');
        var w = Math.min(parseInt(parts[0], 10) || 600, 600);
        var h = Math.round(w * ((parseInt(parts[1], 10) || 600) / (parseInt(parts[0], 10) || 600)));
        img.setAttribute('src', template.replace('{size}', w + 'x' + h));
        img.removeAttribute('srcset');
        img.setAttribute('data-rimg', 'loaded');
      }
    }
  }

  /* ─── Skeleton Placeholders ─── */

  function showSkeletons() {
    if (!state.skeletonTemplate) return;

    var perRow;

    if (state.isBlog) {
      // Blog grid is 3-col desktop, 2-col tablet, 1-col mobile
      perRow = window.innerWidth > 991 ? 3 : (window.innerWidth > 600 ? 2 : 1);
    } else {
      // Detect products per row from grid class (row-of-2, row-of-3, row-of-4)
      perRow = 2;
      var classList = state.grid.className;
      var match = classList.match(/row-of-(\d+)/);
      if (match) perRow = parseInt(match[1], 10);
      if (window.innerWidth < 768) perRow = 2;
    }

    var skeletons = [];
    var skeletonTag = state.isBlog ? 'article' : 'li';
    for (var i = 0; i < perRow; i++) {
      var clone = state.skeletonTemplate.content.cloneNode(true);
      var el = clone.querySelector(skeletonTag) || clone.firstElementChild;
      if (el) {
        state.grid.appendChild(el);
        skeletons.push(el);
      }
    }
    state.skeletons = skeletons;
  }

  function removeSkeletons() {
    if (state.skeletons.length === 0) return;

    // Fade out then remove
    state.skeletons.forEach(function (el) {
      el.classList.add('eb-skeleton--removing');
    });

    var toRemove = state.skeletons.slice();
    state.skeletons = [];

    setTimeout(function () {
      toRemove.forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    }, 150);
  }

  /* ─── Spinner ─── */

  function createSpinner() {
    removeElement('.' + SPINNER_CLASS);

    var spinner = document.createElement('div');
    spinner.className = SPINNER_CLASS;
    spinner.innerHTML =
      '<div class="eb-infinite-scroll__spinner-dots">' +
        '<span class="eb-infinite-scroll__spinner-dot"></span>' +
        '<span class="eb-infinite-scroll__spinner-dot"></span>' +
        '<span class="eb-infinite-scroll__spinner-dot"></span>' +
      '</div>';
    spinner.setAttribute('aria-hidden', 'true');

    state.grid.parentNode.insertBefore(spinner, state.grid.nextSibling);
    state.spinner = spinner;
  }

  function createSentinel() {
    removeElement('.' + SENTINEL_CLASS);

    var sentinel = document.createElement('div');
    sentinel.className = SENTINEL_CLASS;
    sentinel.setAttribute('aria-hidden', 'true');

    var ref = state.spinner || state.grid;
    ref.parentNode.insertBefore(sentinel, ref.nextSibling);
    state.sentinel = sentinel;
  }

  /* ─── Observer ─── */

  function connectObserver() {
    if (state.observer) {
      state.observer.disconnect();
    }

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !state.isLoading) {
          loadNextPage();
        }
      });
    }, {
      rootMargin: '0px 0px ' + PRELOAD_BUFFER + 'px 0px'
    });

    if (state.sentinel) {
      state.observer.observe(state.sentinel);
    }
  }

  /* ─── Momentum Scroll Protection ─── */

  function connectVelocityWatch() {
    if (state.velocityHandler) {
      window.removeEventListener('scroll', state.velocityHandler);
    }

    var lastY = window.scrollY;
    var lastTime = Date.now();

    state.velocityHandler = function () {
      var now = Date.now();
      var dt = now - lastTime;
      if (dt > 50) { // throttle to ~20fps
        var velocity = Math.abs(window.scrollY - lastY) / dt * 1000;
        lastY = window.scrollY;
        lastTime = now;

        // Fast scroll + near bottom = trigger load immediately
        if (velocity > VELOCITY_THRESHOLD && !state.isLoading && state.currentPage < state.totalPages) {
          var distToBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
          if (distToBottom < PRELOAD_BUFFER) {
            loadNextPage();
          }
        }
      }
    };

    window.addEventListener('scroll', state.velocityHandler, { passive: true });
  }

  /* ─── Prefetch Queue ─── */

  function schedulePrefetch() {
    if (state.currentPage >= state.totalPages) return;

    var nextPage = state.currentPage + 1;
    if (state.prefetchedPages[nextPage] || state.prefetchInFlight === nextPage) return;

    var doPrefetch = function () {
      if (state.prefetchedPages[nextPage] || state.prefetchInFlight === nextPage) return;
      if (state.currentPage >= state.totalPages) return;

      state.prefetchInFlight = nextPage;

      // Cancel any previous prefetch
      if (state.prefetchController) {
        state.prefetchController.abort();
      }
      state.prefetchController = new AbortController();

      fetch(buildUrl(nextPage), { signal: state.prefetchController.signal })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (html) {
            state.prefetchedPages[nextPage] = html;
          }
          state.prefetchInFlight = null;
        })
        .catch(function () {
          state.prefetchInFlight = null;
        });
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(doPrefetch, { timeout: 2000 });
    } else {
      setTimeout(doPrefetch, 100);
    }
  }

  /* ─── Load Next Page ─── */

  function loadNextPage() {
    if (state.isLoading || state.currentPage >= state.totalPages) return;

    state.isLoading = true;
    removeElement('.' + RETRY_CLASS);

    var nextPage = state.currentPage + 1;

    // Show skeletons immediately (0ms visual feedback)
    showSkeletons();

    // Check prefetch cache first
    if (state.prefetchedPages[nextPage]) {
      var cachedHtml = state.prefetchedPages[nextPage];
      delete state.prefetchedPages[nextPage];

      removeSkeletons();
      appendItemsFromHtml(cachedHtml);
      onPageLoaded(nextPage);
      return;
    }

    // Not cached — fetch via Section Rendering API
    var url = buildUrl(nextPage);

    if (state.abortController) {
      state.abortController.abort();
    }
    state.abortController = new AbortController();

    var timeoutId = setTimeout(function () {
      if (state.abortController) state.abortController.abort();
    }, FETCH_TIMEOUT);

    fetch(url, { signal: state.abortController.signal })
      .then(function (response) {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        removeSkeletons();
        appendItemsFromHtml(html);
        onPageLoaded(nextPage);
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        removeSkeletons();
        if (err.name === 'AbortError') {
          hideSpinner();
          state.isLoading = false;
          return;
        }
        hideSpinner();
        state.isLoading = false;
        showRetry();
      });
  }

  function onPageLoaded(page) {
    state.currentPage = page;
    saveState();
    reinitComponents();
    updateRelNext();

    if (state.currentPage >= state.totalPages) {
      onLastPage();
    } else {
      hideSpinner();
      state.isLoading = false;
      // Prefetch the next-next page during idle
      schedulePrefetch();
    }
  }

  /* ─── URL Builder (Section Rendering API) ─── */

  function buildUrl(page) {
    var params = new URLSearchParams(window.location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }

    // Use Section Rendering API for smaller payloads (~85% reduction)
    if (state.sectionId) {
      params.set('section_id', state.sectionId);
    }

    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  /* ─── Spinner Control ─── */

  function showSpinner() {
    if (state.spinner) {
      state.spinner.classList.add(SPINNER_VISIBLE);
    }
  }

  function hideSpinner() {
    if (state.spinner) {
      state.spinner.classList.remove(SPINNER_VISIBLE);
    }
  }

  function onLastPage() {
    hideSpinner();
    state.isLoading = false;

    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    removeElement('.' + SENTINEL_CLASS);
    removeElement('.' + SPINNER_CLASS);
  }

  /* ─── Retry ─── */

  function showRetry() {
    removeElement('.' + RETRY_CLASS);

    var retry = document.createElement('div');
    retry.className = RETRY_CLASS;

    var btn = document.createElement('button');
    btn.type = 'button';

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      btn.textContent = 'You appear to be offline. Tap to retry';
    } else {
      btn.textContent = 'Something went wrong. Tap to retry';
    }

    btn.addEventListener('click', function () {
      removeElement('.' + RETRY_CLASS);
      loadNextPage();
    });

    retry.appendChild(btn);
    var ref = state.spinner || state.grid;
    ref.parentNode.insertBefore(retry, ref.nextSibling);
  }

  /* ─── Component Reinit ─── */

  /**
   * Initialize infinite-scroll products using Atlantic's own ProductItem class.
   * We exposed it globally via window.AtlanticProductItem in atlantic.js.
   * This gives us identical behavior to first-page products: same add-to-cart
   * animation, same CartMini refresh/open, same Quickshop — zero custom code.
   */
  function initNewProductItems() {
    if (typeof window.AtlanticProductItem === 'undefined') return;

    var newItems = state.grid.querySelectorAll('[data-product-item]:not([data-eb-initialized])');
    newItems.forEach(function (el) {
      el.setAttribute('data-eb-initialized', 'true');
      new window.AtlanticProductItem(el);
    });
  }

  function reinitComponents() {
    // Initialize add-to-cart/quickshop on new products (skip for blog and collection-list)
    if (!state.isBlog && !state.isCollectionList) {
      initNewProductItems();
    }

    if (typeof AOS !== 'undefined' && AOS.refreshHard) {
      AOS.refreshHard();
    }

    if (typeof theme !== 'undefined') {
      if (theme.collapsibles && typeof theme.collapsibles.init === 'function') {
        theme.collapsibles.init();
      }
    }

    document.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', {
      detail: { page: state.currentPage }
    }));
  }

  function updateRelNext() {
    var existing = document.querySelector('link[rel="next"]');
    if (state.currentPage >= state.totalPages) {
      if (existing) existing.remove();
    } else {
      // Build URL without section_id for SEO
      var params = new URLSearchParams(window.location.search);
      params.set('page', state.currentPage + 1);
      var nextUrl = window.location.pathname + '?' + params.toString();
      if (existing) {
        existing.setAttribute('href', nextUrl);
      }
    }
  }

  /* ─── Utilities ─── */

  function removeElement(selector) {
    var el = document.querySelector(selector);
    if (el) el.remove();
  }

  /* ─── Reset (filter/sort AJAX reload) ─── */

  function reset() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    if (state.imageObserver) {
      state.imageObserver.disconnect();
      state.imageObserver = null;
    }

    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }

    if (state.prefetchController) {
      state.prefetchController.abort();
      state.prefetchController = null;
    }

    state.isLoading = false;
    state.prefetchedPages = {};
    state.prefetchInFlight = null;

    removeSkeletons();
    removeElement('.' + SENTINEL_CLASS);
    removeElement('.' + SPINNER_CLASS);
    removeElement('.' + END_CLASS);
    removeElement('.' + RETRY_CLASS);

    state.sentinel = null;
    state.spinner = null;

    state.grid = null;
    if (state.container) {
      state.grid = state.container.querySelector('.collection-content__wrapper .product-list') ||
                   state.container.querySelector('.product-list') ||
                   state.container.querySelector('.eb-blog-grid') ||
                   state.container.querySelector('section.collections');
    }

    if (!state.grid) return;

    var pagination = state.container.querySelector('.pagination[data-eb-total-pages]');
    if (!pagination) return;

    state.totalPages = parseInt(pagination.getAttribute('data-eb-total-pages'), 10) || 1;
    state.currentPage = parseInt(pagination.getAttribute('data-eb-current-page'), 10) || 1;

    pagination.style.display = 'none';

    if (state.totalPages <= 1 || state.currentPage >= state.totalPages) return;

    createSpinner();
    createSentinel();
    connectObserver();
    schedulePrefetch();
  }

  // Listen for filter/sort AJAX reloads
  document.addEventListener('collection:reloaded', reset);

  /* Leaving the page. 'pagehide' rather than 'beforeunload' — a beforeunload
     listener makes the page ineligible for the back-forward cache, and bfcache
     is the best possible version of this feature: the browser hands the whole
     page back on Back, already scrolled, with nothing refetched. This listener
     also runs when the page is merely frozen into that cache, so it only
     aborts in-flight work — it never tears anything down. */
  window.addEventListener('pagehide', function () {
    /* Deliberately no saveState() here: by the time this fires the page has
       often already been scrolled back to the top, so the reading would be
       index 0 and would overwrite the good value the debounced scroll
       handler recorded on the way out. */
    if (state.prefetchController) {
      state.prefetchController.abort();
    }
    if (state.abortController) {
      state.abortController.abort();
    }
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
